import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from 'react-tooltip';
import { StatusIcon } from "../../../components/StatusIcon";
import { Data } from "../../../service/global";
import { MDBCard, MDBCardBody, MDBIcon } from "mdb-react-ui-kit";
import { NavLink } from "react-router-dom";
import OpLocationListItemDTO from "@common/dtos/OpLocationListItemDTO";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { FixedSizeList, FixedSizeList as List } from "react-window";
import { SearchInput } from "../../../components/SearchInput";

export interface LocationsListProps {
    locations: OpLocationListItemDTO[];
}

export const LocationsList = ({locations}: LocationsListProps) => {
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();
    const [containerHeight, setContainerHeight] = useState(0);
    const [search, setSearch] = useState('');
    const twoColumnDisplay = useMemo(() => innerWidth >= 1200, [innerWidth]);

    const sortedLocationsList = useMemo(() => 
        !locations ? [] : locations
            .filter(location => location.Name.toLowerCase().includes(search.toLowerCase()))
            .map(location => ({
                location,
                priority: location.status.map(s => Data.esList[s.id]?.priority ?? 0).sort((a,b) => b-a)[0] ?? (
                    ((location.poweredOn ?? 0) < (location.nrOfMachines ?? 0) ||
                    (location.connected ?? 0) < (location.nrOfMachines ?? 0)) ? 1 : -1
                ),
            }) as { location: OpLocationListItemDTO, priority: number })
            .sort((a,b) => b.priority - a.priority)
            .map(({location}) => location)

    , [locations, search]);

    const logContainerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<FixedSizeList>(null);

    useEffect(() => {
        if (!logContainerRef.current) return;
        const observer = new ResizeObserver(() => {
            setContainerHeight(logContainerRef.current?.getBoundingClientRect().height || 0);
        });
        observer.observe(logContainerRef.current);
        return () => observer.disconnect();
    }, [logContainerRef]);

    if (!locations || locations.length === 0) return <span className="text-muted fst-italic w-100 text-center">{t('no_locations')}</span>;
    return (
        <div className="d-flex flex-column w-100 flex-fill overflow-hidden">
            <div className={`px-2 mb-2 d-flex flex-row justify-content-between ${twoColumnDisplay ? 'w-50' : 'w-100'}`}>
                <SearchInput onSearch={setSearch} className="" style={{maxWidth: '100%'}}/>
            </div>
            <div ref={logContainerRef} className="d-flex flex-column flex-fill overflow-auto w-100 px-2">
                {(!locations || locations.length === 0) &&
                    <span className="text-muted fst-italic w-100 text-center">{t('no_locations')}</span>
                }
                {locations?.length > 0 && <List
                    ref={listRef}
                    height={containerHeight}
                    width="100%"
                    itemSize={70}
                    itemCount={twoColumnDisplay ? Math.ceil(sortedLocationsList.length/2) : sortedLocationsList.length}
                >
                    {({index, style}) => (
                        <>
                            {!twoColumnDisplay && (
                                <div style={style} className="overflow-hidden py-1 px-1 w-100">
                                    <LocationItem location={sortedLocationsList[index]}/>
                                </div>
                            )}
                            {twoColumnDisplay && (
                                <div className="d-flex flex-row w-100 overflow-hidden" style={style}>
                                    <div className="d-flex w-50 px-1">
                                        <LocationItem location={sortedLocationsList[2*index]}/>
                                    </div>
                                    
                                    {(2*index+1 < sortedLocationsList.length) && <div className="d-flex w-50 px-1">
                                        <LocationItem location={sortedLocationsList[2*index+1]}/>
                                    </div>}
                                </div>
                            )}
                        </>
                    )}
                </List>}
            </div>
        </div>
    )
}

const LocationItem = ({location}: {location: OpLocationListItemDTO}) => {
    const { t } = useTranslation();
    const poweredIssue = useMemo(() => (location.poweredOn ?? 0) < (location.nrOfMachines ?? 0), [location]);
    const connectedIssue = useMemo(() => (location.connected ?? 0) < (location.nrOfMachines ?? 0), [location]);
    const bg = useMemo(() =>
        Data.esList[location.status[0]?.id]?.web?.level ||
        ( (poweredIssue || connectedIssue) ? "gray" : null)
    , [location]);


    return (
        <div className="d-inline-block w-100">
            <MDBCard className="card-hover2 shadow-2-strong bg-opacity-25 location-item" background={bg}>
                <NavLink to={`locations/${location.id}${location.singleMachineId ? `/machines/${location.singleMachineId}` : ''}`}>
                    <MDBCardBody className="p-1 w-100 h-100">
                            <div className="d-flex flex-column flex-fill overflow-hidden ps-1">
                                <div className="d-flex flex-row align-items-center w-100 pe-2">
                                    { !location.serviceLocation && <MDBIcon icon="map-marker-alt" size="lg" color="primary" className="" fixed/> }
                                    { location.serviceLocation && <MDBIcon icon="gear" size="lg" color="primary" className="" fixed/> }
                                    <span className="flex-fill text-dark overflow-hidden fw-bold">
                                        { location.defaultLocation && <span><MDBIcon icon="bookmark" color="warning" className="me-1"/></span> }
                                        { location.Name }
                                    </span>

                                    {location.status.map(s => (
                                        <StatusIcon key={s.id} name={s.id} count={s.count} ts={s.ts} badge={location.status.length == 1}/>
                                    ))}
                                </div>
                                <div className="d-flex flex-row w-100 pb-1">
                                    <div className="d-flex flex-row flex-fill justify-content-start align-items-center">
                                        <span
                                            className={`me-2 badge badge-${poweredIssue ? 'danger' : 'success'} me-2`}
                                            title={t(`powered_on`)}
                                            // data-tooltip-id={location.id+'-power'}
                                            // data-tooltip-content={t(`powered_on`)}
                                        >
                                            <MDBIcon icon="power-off" color={`${poweredIssue ? 'danger' : 'success'}`} className="me-1"/>
                                            <span>{location.poweredOn}/{location.nrOfMachines}</span>
                                        </span>
                                        {/* <Tooltip id={location.id+'-power'} place="right"/> */}
                                        <span
                                            className={`me-2 badge badge-${connectedIssue ? 'danger' : 'success'} me-2`}
                                            title={t(`status:CONNECTION_ON`)}
                                            // data-tooltip-id={location.id+'-connection'}
                                            // data-tooltip-content={t(`status:CONNECTION_ON`)}
                                        >
                                            <MDBIcon icon="signal" color={`${connectedIssue ? 'danger' : 'success'}`} className="me-1"/>
                                            <span>{location.connected}/{location.nrOfMachines}</span>
                                        </span>
                                        {/* <Tooltip id={location.id+'-connection'} place="right"/> */}
                                    </div>

                                    <span className="d-flex flex-row align-items-center w-100 flex-fill justify-content-end">
                                        {typeof location.value == 'number' && <>
                                            <span
                                                className="badge badge-primary me-2 mt-1"
                                                title={t('value')}
                                                // data-tooltip-id={location.id+'-value'}
                                                // data-tooltip-content={t('value')}
                                            >
                                                <MDBIcon icon="hand-holding-dollar" color="primary" className="me-1"/>
                                                <span>{location.value}</span>
                                            </span>
                                            {/* <Tooltip id={location.id+'-value'} place="left"/>  */}
                                        </>}
                                    </span>
                                </div>
                            </div>
                    </MDBCardBody>
                </NavLink>
                
            </MDBCard>
        </div>

    );
}


